/**
 * BotBase - Abstract base class for all AI bots.
 * Provides role, state, zone restriction, and separation logic.
 * Extended by ChaserBot, DefenderBot, and StrikerBot.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { loadFBXCharacter, HumanoidAnimator, KickType, CelebrationType } from "./HumanoidMesh";
import { GOAL_HALF_WIDTH } from "./Goal";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";

// Shared AI constants - IDENTICAL across all difficulty modes (no cheating)
const AI_SPEED = 5.5;
const SPRINT_SPEED = 8.8;        // 10% slower than player's 9.68
const CHASE_RADIUS = 25;
const KICK_RANGE = 2.0;
const KICK_POWER = 12;
const KICK_COOLDOWN = 0.8;
const DRIBBLE_RANGE = 3;
const SHOOT_RANGE = 15;
const GOAL_Z = -30;

// Tackle settings
const TACKLE_RANGE = 2.5;
const TACKLE_COOLDOWN = 1.5;
const TACKLE_DURATION = 0.3;
const TACKLE_SPEED = 10;

// Possession system
const POSSESSION_LOSE_RANGE = 3.5;

// Stamina system (same as player for fairness)
const STAMINA_MAX = 120;         // Slightly less than player's 150
const STAMINA_DRAIN = 18;        // Slower than player's 15
const STAMINA_REGEN = 25;        // Faster than player's 30

/**
 * Abstract base class for all AI bots
 */
export abstract class BotBase {
  public mesh: BABYLON.Mesh;
  public animator: HumanoidAnimator;
  public hasBallPossession = false;
  public isTackling = false;
  public stamina = STAMINA_MAX;
  public isGoalCelebration = false;  // Flag to disable ball interaction during celebration
  
  protected _role: AIRole;
  protected _state: AIState = AIState.IDLE;
  protected _homePosition: BABYLON.Vector3;
  protected _allowedZone: AllowedZone;
  protected _separationConfig: SeparationConfig;
  
  protected _ball: Ball;
  protected _player: Player | null = null;
  protected _scene: BABYLON.Scene;
  protected _collider: BABYLON.Mesh;
  protected _kickTimer = 0;
  protected _tackleTimer = 0;
  protected _tackleCooldown = 0;
  protected _currentSpeed = 0;
  protected _velocity = new BABYLON.Vector3();
  protected _tackleDir = new BABYLON.Vector3();
  protected _tmpVec = new BABYLON.Vector3();
  protected _shadowGenerator?: BABYLON.ShadowGenerator;
  protected _isSprinting = false;

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    role: AIRole,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player,
    meshName: string = "bot"
  ) {
    this._scene = scene;
    this._ball = ball;
    this._role = role;
    this._homePosition = homePosition.clone();
    this._allowedZone = allowedZone;
    this._separationConfig = separationConfig;
    this._shadowGenerator = shadowGenerator;
    this._player = player || null;

    // Create placeholder root immediately for positioning
    this.mesh = BABYLON.MeshBuilder.CreateBox(`${meshName}_placeholder`, { size: 0.5 }, scene);
    this.mesh.isVisible = true; // Show placeholder while loading
    const placeholderMat = new BABYLON.StandardMaterial(`${meshName}_placeholder_mat`, scene);
    placeholderMat.diffuseColor = this._getJerseyColor();
    this.mesh.material = placeholderMat;
    this.mesh.position = this._homePosition.clone();

    // Dummy animator until FBX loads
    this.animator = new HumanoidAnimator(null as any);

    // Load FBX character model asynchronously
    loadFBXCharacter(meshName, scene, {
        jersey: this._getJerseyColor(),
        skin: new BABYLON.Color3(0.8, 0.7, 0.6),
    }, shadowGenerator).then(({ root, animations }) => {
      root.position.copyFrom(this.mesh.position);
      root.rotation.copyFrom(this.mesh.rotation);
      this.mesh.dispose();
      this.mesh = root;
      this.animator.setFBXAnimations(animations);
      console.log(`${meshName} FBX model loaded!`);
    });

    // Invisible physics collider
    this._collider = BABYLON.MeshBuilder.CreateCylinder(
      `${meshName}Collider`,
      { height: 1.6, diameter: 0.6, tessellation: 10 },
      scene
    );
    this._collider.isVisible = false;
    this._collider.physicsImpostor = new BABYLON.PhysicsImpostor(
      this._collider,
      BABYLON.PhysicsImpostor.CylinderImpostor,
      { mass: 0, restitution: 0.3, friction: 0.2 },
      scene
    );
  }

  /**
   * Get jersey color based on role (override in subclasses)
   */
  protected abstract _getJerseyColor(): BABYLON.Color3;

  /**
   * Main update loop - handles state machine, movement, separation
   */
  update(dt: number, teammates: BotBase[]): void {
    // During goal celebration, just animate and return to home
    if (this.isGoalCelebration) {
      this._currentSpeed = 0;
      this.animator.animate(dt, 0, false);
      this._syncCollider();
      return;
    }
    
    if (this._kickTimer > 0) this._kickTimer -= dt;
    if (this._tackleCooldown > 0) this._tackleCooldown -= dt;
    if (this._tackleTimer > 0) {
      this._updateTackle(dt);
      this.animator.animate(dt, this._currentSpeed, this.animator.isKicking);
      this._syncCollider();
      return;
    }

    const ballPos = this._ball.mesh.position;
    const myPos = this.mesh.position;

    // Calculate distances
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const distToBall = Math.sqrt(dx * dx + dz * dz);
    const distToGoal = Math.abs(myPos.z - GOAL_Z);

    // Update possession status
    this._updatePossession(distToBall);

    // AI Decision Making (override in subclasses)
    this._makeDecision(distToBall, distToGoal, ballPos, myPos);

    // Execute behavior based on state
    this._handleState(ballPos, myPos, distToBall, distToGoal, dt);

    // Apply separation from teammates (anti-overlap)
    this._applySeparation(teammates, dt);

    // Apply zone restriction
    this._applyZoneRestriction();

    // Handle stamina
    this._handleStamina(dt);

    this.animator.animate(dt, this._currentSpeed, this.animator.isKicking);
    this._syncCollider();
  }

  /**
   * Handle stamina - drain when sprinting, regen when not
   */
  protected _handleStamina(dt: number): void {
    if (this._isSprinting || this._tackleTimer > 0) {
      this.stamina -= STAMINA_DRAIN * dt;
      if (this.stamina < 0) this.stamina = 0;
    } else {
      this.stamina += STAMINA_REGEN * dt;
      if (this.stamina > STAMINA_MAX) this.stamina = STAMINA_MAX;
    }
  }

  /**
   * Handle current state behavior (override in subclasses for role-specific behavior)
   */
  protected _handleState(
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3,
    distToBall: number,
    distToGoal: number,
    dt: number
  ): void {
    switch (this._state) {
      case AIState.CHASE_BALL:
        this._chaseBall(ballPos, distToBall, dt);
        break;
      case AIState.DRIBBLE:
        this._dribble(ballPos, distToGoal, dt);
        break;
      case AIState.SHOOT:
        this._shoot(ballPos, myPos);
        break;
      case AIState.TACKLE:
        this._startTackle(myPos, ballPos);
        break;
      case AIState.RETURN_HOME:
        this._returnHome(dt);
        break;
      case AIState.IDLE:
      default:
        this._currentSpeed = 0;
        break;
    }
  }

  /**
   * Make AI decision (override in subclasses for role-specific behavior)
   */
  protected _makeDecision(
    distToBall: number,
    distToGoal: number,
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3
  ): void {
    // If ball is far away, return to home position
    if (distToBall > CHASE_RADIUS) {
      this._state = AIState.RETURN_HOME;
      return;
    }

    // Check if player has possession and we can tackle
    if (this._player && this._player.hasBallPossession && distToBall < TACKLE_RANGE && this._tackleCooldown <= 0) {
      this._state = AIState.TACKLE;
      return;
    }

    // If close to ball, decide what to do
    if (distToBall < KICK_RANGE) {
      // Check if we can shoot at goal
      if (distToGoal < SHOOT_RANGE && Math.abs(myPos.x) < GOAL_HALF_WIDTH + 2) {
        this._state = AIState.SHOOT;
      } else if (this.hasBallPossession && distToBall < DRIBBLE_RANGE) {
        this._state = AIState.DRIBBLE;
      } else {
        this._state = AIState.CHASE_BALL;
      }
    } else {
      this._state = AIState.CHASE_BALL;
    }
  }

  /**
   * Apply separation force from nearby teammates (anti-overlap)
   */
  protected _applySeparation(teammates: BotBase[], dt: number): void {
    if (!teammates || teammates.length === 0) return;

    const myPos = this.mesh.position;
    const separationVector = new BABYLON.Vector3(0, 0, 0);
    let neighborCount = 0;

    for (const teammate of teammates) {
      if (teammate === this) continue;

      const teammatePos = teammate.mesh.position;
      const dx = myPos.x - teammatePos.x;
      const dz = myPos.z - teammatePos.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < this._separationConfig.minDistance && dist > 0.01) {
        // Normalize and weight by inverse distance
        const weight = 1 - (dist / this._separationConfig.minDistance);
        separationVector.x += (dx / dist) * weight;
        separationVector.z += (dz / dist) * weight;
        neighborCount++;
      }
    }

    if (neighborCount > 0) {
      // Apply smooth separation force
      const force = this._separationConfig.separationForce * dt;
      myPos.x += separationVector.x * force;
      myPos.z += separationVector.z * force;
    }
  }

  /**
   * Apply zone restriction with smooth clamping
   */
  protected _applyZoneRestriction(): void {
    const pos = this.mesh.position;
    pos.x = BABYLON.Scalar.Clamp(pos.x, this._allowedZone.minX, this._allowedZone.maxX);
    pos.z = BABYLON.Scalar.Clamp(pos.z, this._allowedZone.minZ, this._allowedZone.maxZ);
  }

  /**
   * Update possession status
   */
  protected _updatePossession(distToBall: number): void {
    // Lose possession if ball gets too far
    if (this.hasBallPossession && distToBall > POSSESSION_LOSE_RANGE) {
      this.hasBallPossession = false;
    }
  }

  /**
   * Called when player tackles this AI
   */
  public losePossession(): void {
    this.hasBallPossession = false;
  }

  /**
   * Get current role
   */
  get role(): AIRole {
    return this._role;
  }

  /**
   * Get current state
   */
  get state(): AIState {
    return this._state;
  }

  /**
   * Set role (for dynamic role changes if needed)
   */
  setRole(role: AIRole): void {
    this._role = role;
  }

  // ─── Common AI Behaviors (shared across all bots) ────────────────────────────

  protected _chaseBall(ballPos: BABYLON.Vector3, distToBall: number, dt: number): void {
    const myPos = this.mesh.position;
    
    // Sprint when far from ball and have stamina
    this._isSprinting = distToBall > 5 && this.stamina > 10;
    const speed = this._isSprinting ? SPRINT_SPEED : AI_SPEED;
    
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.1) {
      const dirX = dx / dist;
      const dirZ = dz / dist;
      
      myPos.x += dirX * speed * dt;
      myPos.z += dirZ * speed * dt;
      this._currentSpeed = speed;
      this._velocity.set(dirX * speed, 0, dirZ * speed);
      
      const angle = Math.atan2(dirX, dirZ);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
    }
  }

  protected _dribble(ballPos: BABYLON.Vector3, distToGoal: number, dt: number): void {
    const myPos = this.mesh.position;
    
    // Calculate direction toward goal
    const goalDx = -myPos.x;
    const goalDz = GOAL_Z - myPos.z;
    
    const goalDist = Math.sqrt(goalDx * goalDx + goalDz * goalDz);
    const dirX = goalDx / goalDist;
    const dirZ = goalDz / goalDist;
    
    // Dribble - small kicks toward goal
    if (this._kickTimer <= 0) {
      this._tmpVec.set(dirX * 0.5, 0.1, dirZ * 0.5).normalize();
      this._ball.kick(this._tmpVec, 3, this._velocity);
      this._kickTimer = KICK_COOLDOWN * 0.5;
      this.animator.triggerKick(KickType.Normal);
    }
    
    // Move toward ball to maintain control
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.3) {
      const vx = (dx / dist) * AI_SPEED;
      const vz = (dz / dist) * AI_SPEED;
      myPos.x += vx * dt;
      myPos.z += vz * dt;
      this._velocity.set(vx, 0, vz);
    } else {
      this._velocity.set(0, 0, 0);
    }
    
    this._currentSpeed = AI_SPEED;
    
    const angle = Math.atan2(dirX, dirZ);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);
  }

  protected _shoot(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3): void {
    if (this._kickTimer > 0) return;
    
    const targetX = (Math.random() - 0.5) * 2;
    const targetZ = GOAL_Z;
    
    const dx = targetX - myPos.x;
    const dz = targetZ - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    const power = Math.min(KICK_POWER, dist * 0.4 + 8);
    this._tmpVec.set(dx / dist, 0.15 + Math.random() * 0.1, dz / dist).normalize();
    this._ball.kick(this._tmpVec, power, this._velocity);
    
    this._kickTimer = KICK_COOLDOWN;
    this.animator.triggerKick(KickType.Power);
    this.hasBallPossession = false;
    this._state = AIState.CHASE_BALL;
  }

  protected _startTackle(myPos: BABYLON.Vector3, ballPos: BABYLON.Vector3): void {
    if (this._tackleCooldown > 0) return;

    this._tackleTimer = TACKLE_DURATION;
    this._tackleCooldown = TACKLE_COOLDOWN;
    this.isTackling = true;

    // Calculate direction to ball for tackle
    const dx = ballPos.x - myPos.x;
    const dz = ballPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    this._tackleDir.set(dx / dist, 0, dz / dist);
  }

  protected _updateTackle(dt: number): void {
    this._tackleTimer -= dt;
    this._currentSpeed = TACKLE_SPEED;

    // Slide in tackle direction
    const vx = this._tackleDir.x * TACKLE_SPEED;
    const vz = this._tackleDir.z * TACKLE_SPEED;
    this.mesh.position.addInPlace(new BABYLON.Vector3(vx * dt, 0, vz * dt));
    this._velocity.set(vx, 0, vz);

    // Check if we hit the ball
    const toBall = this._ball.mesh.position.subtract(this.mesh.position);
    toBall.y = 0;
    const dist = toBall.length();

    if (dist < TACKLE_RANGE) {
      // Steal the ball! Knock it away and gain possession
      this._ball.kick(this._tackleDir.add(new BABYLON.Vector3(0, 0.1, 0)), 4, this._velocity);
      this.hasBallPossession = true;
      
      // Make player lose possession if we tackled them
      if (this._player && this._player.hasBallPossession) {
        this._player.losePossession();
      }
    }

    if (this._tackleTimer <= 0) {
      this.isTackling = false;
    }
  }

  protected _returnHome(dt: number): void {
    const myPos = this.mesh.position;
    const homeX = this._homePosition.x;
    const homeZ = this._homePosition.z;
    
    const dx = homeX - myPos.x;
    const dz = homeZ - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.5) {
      const speed = AI_SPEED * 0.7;
      const vx = (dx / dist) * speed;
      const vz = (dz / dist) * speed;
      myPos.x += vx * dt;
      myPos.z += vz * dt;
      this._currentSpeed = speed;
      this._velocity.set(vx, 0, vz);
      
      const angle = Math.atan2(dx, dz);
      this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.05);
    } else {
      this._currentSpeed = 0;
      this._state = AIState.IDLE;
    }
  }

  protected _syncCollider(): void {
    this._collider.position.set(
      this.mesh.position.x,
      0.8,
      this.mesh.position.z
    );
  }

  /**
   * Reset bot to home position
   */
  resetPosition(): void {
    this.mesh.position = this._homePosition.clone();
    this.mesh.rotation.y = 0;
    this._kickTimer = 0;
    this._tackleTimer = 0;
    this._tackleCooldown = 0;
    this.isTackling = false;
    this.hasBallPossession = false;
    this._state = AIState.IDLE;
    this._isSprinting = false;
    this.stamina = STAMINA_MAX;
    this._syncCollider();
  }

  /**
   * Trigger celebration animation
   */
  celebrate(): void {
    this.animator.triggerCelebration(CelebrationType.Jump);
  }
}